import { z } from 'zod';
import { SchemaFormDefinition, SchemaFormFields, SelectField } from '@mews-commander/form';
import { BusinessError, FormInputField, lt } from '@mews/framework';
import { Banner, Container, Divider, EmptyState, Stack, StackItem, Typography } from '@optimus-web/core';
import {
    ReservationContractExternalTemplateFieldType,
    ReservationContractTemplateFileFieldIdentifier,
} from '@mews-commander/data-access';
import { GroupOption } from '@mews-commander/types';
import { addFieldPrefix, getAllSortedDocumentFields } from '../utils';
import { TemplateDocumentFields } from '../hooks/useContractFieldsQuery';
import { useTemplateDataMappingOptions } from '../hooks/useTemplateDataMappingOptions';
import { TemplateFormStepFields } from './constants';
import { FieldStatusIcon } from './FieldStatusLabel';

const TemplateMappingRow = ({ label, content }: { label: React.ReactNode; content: React.ReactNode }) => (
    <Stack distribution="space-between" spacing="200" itemAlignment="center">
        <StackItem grow maxWidth="295px">
            {label}
        </StackItem>
        <StackItem grow maxWidth="295px">
            {content}
        </StackItem>
    </Stack>
);

export const createDataMappingSchema = ({ fieldsData }: { fieldsData: TemplateDocumentFields[] }) => {
    const allFields = getAllSortedDocumentFields(fieldsData);
    return z.object(
        Object.fromEntries(
            allFields.map(({ id }) => [
                addFieldPrefix(id),
                z.nativeEnum(ReservationContractTemplateFileFieldIdentifier).optional().nullable(),
            ])
        )
    );
};

export type DataMappingValues = z.infer<ReturnType<typeof createDataMappingSchema>>;

export const createDataMappingFormDefinition = ({
    fieldsData,
    templateDataMappingOptions,
}: {
    fieldsData: TemplateDocumentFields[];
    templateDataMappingOptions: GroupOption[];
}): SchemaFormDefinition<ReturnType<typeof createDataMappingSchema>> => {
    const allFields = getAllSortedDocumentFields(fieldsData);
    return {
        fields: Object.fromEntries(
            allFields.map((field, index) => {
                const showDocumentName = index === 0 || allFields[index - 1].documentId !== field.documentId;
                const showPageLabel = showDocumentName || allFields[index - 1].pageNumber !== field.pageNumber;

                return [
                    addFieldPrefix(field.id),
                    {
                        render: (props) => (
                            <>
                                {showDocumentName && (
                                    <Container padding={['200', '0', '0', '0']}>
                                        <Typography textStyle="bodyMediumStrong" variant="secondary" as="h2">
                                            {field.documentName}
                                        </Typography>
                                    </Container>
                                )}
                                {showPageLabel && (
                                    <>
                                        <Divider>
                                            <Typography textStyle="bodySmallStrong" variant="secondary" as="h3">
                                                {lt('ContractsDocumentPageLabel', {
                                                    pageNumber: field.pageNumber,
                                                    totalPage: field.totalPages,
                                                })}
                                            </Typography>
                                        </Divider>
                                        <TemplateMappingRow
                                            label={
                                                <Typography textStyle="bodyMedium" variant="tertiary" as="p">
                                                    {lt('ContractTemplateDataPlaceholderText')}
                                                </Typography>
                                            }
                                            content={
                                                <Typography textStyle="bodyMedium" variant="tertiary" as="p">
                                                    {lt('ContractTemplateDataFillWithInputText')}
                                                </Typography>
                                            }
                                        />
                                    </>
                                )}
                                <TemplateMappingRow
                                    label={
                                        <Stack itemAlignment="center" spacing="50">
                                            <Typography textStyle="bodyLarge" as="p">
                                                {field.label}
                                            </Typography>
                                            <FieldStatusIcon fieldStatus={field.status} />
                                        </Stack>
                                    }
                                    content={
                                        <>
                                            {field.type !== ReservationContractExternalTemplateFieldType.Checkbox &&
                                                field.type !== ReservationContractExternalTemplateFieldType.Radio && (
                                                    <SelectField
                                                        {...props}
                                                        options={templateDataMappingOptions}
                                                        showEmptyValue={false}
                                                    />
                                                )}
                                            {field.type === ReservationContractExternalTemplateFieldType.Checkbox && (
                                                <FormInputField
                                                    {...props}
                                                    readOnly
                                                    placeholder={lt('ContractTemplateTabIdentifierOptionLabelCheckbox')}
                                                />
                                            )}
                                            {field.type === ReservationContractExternalTemplateFieldType.Radio && (
                                                <FormInputField
                                                    {...props}
                                                    readOnly
                                                    placeholder={lt(
                                                        'ContractTemplateTabIdentifierOptionLabelRadioGroup'
                                                    )}
                                                />
                                            )}
                                        </>
                                    }
                                />
                            </>
                        ),
                    },
                ];
            })
        ),
    };
};

export const DataMappingForm = ({ contractFieldsData }: { contractFieldsData?: TemplateDocumentFields[] | null }) => {
    if (contractFieldsData == null) {
        throw new BusinessError('Fields data is not available');
    }
    const templateDataMappingOptions = useTemplateDataMappingOptions();

    const removedFields = contractFieldsData.flatMap((document) => document.removedFields ?? []);
    const removedFieldLabels = removedFields.map((field) => `"${field.label}"`).join(', ');

    const isNoFields = contractFieldsData.every((document) => document.fields.length === 0);

    return (
        <Stack vertical spacing="200">
            <Typography textStyle="titleLargeStrong" as="h2">
                {lt('ContractTemplateDataMappingTitle')}
            </Typography>
            {removedFields.length > 0 && (
                <Banner
                    variant="warning"
                    primaryText={lt('ContractTemplateDeletedFieldsBannerTitle')}
                    secondaryText={removedFieldLabels}
                />
            )}
            {isNoFields && (
                <EmptyState
                    primaryText={lt('ContractsTemplateNoFieldsPrimaryText')}
                    secondaryText={lt('ContractsTemplateNoFieldsSecondaryText')}
                />
            )}
            <SchemaFormFields
                schema={createDataMappingSchema({ fieldsData: contractFieldsData })}
                formDefinition={createDataMappingFormDefinition({
                    fieldsData: contractFieldsData,
                    templateDataMappingOptions,
                })}
                namePrefix={TemplateFormStepFields.dataMappingFields}
            />
        </Stack>
    );
};
